/* eslint-disable max-lines */
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { StyledAnchor } from "shared/components/common/base-link/styles";
import type { WithStickyStackedHeightProps } from "shared/lib/styles";
import {
  fontCreteRound,
  fontHeebo,
  fontMerriweather,
  fontRoboto,
  fontSourceSerif4,
  setFullViewportWidthStyles,
  theme,
} from "shared/lib/styles";

import {
  AdSlotContainer,
  Container as AdSlotOuterContainer,
  StyledSlot,
} from "scmp-app/components/advertisement/ad-slots/ad-slot/styles";
import { getClassNameFromSize } from "scmp-app/components/advertisement/ad-slots/helpers";
import { StyledAuthorAvatar } from "scmp-app/components/article/article-author-block/author-avatars/styles";
import {
  AndSeparator,
  AuthorLocation,
  AuthorNamesContainer,
  CommaSeparator,
} from "scmp-app/components/article/article-author-block/author-names/styles";
import {
  Item,
  LatestCorrectionDate,
  LatestCorrectionDateContainer,
} from "scmp-app/components/article/article-corrections-widget/styles";
import { ArticleGoogleNewsButton } from "scmp-app/components/article/article-google-news-button";
import { ArticleHeaderAdSlot } from "scmp-app/components/article/article-header-ad-slot";
import {
  ArticleImageContainer,
  DescriptionContainer as ArticleImageDescriptionContainer,
  DescriptionContainer,
} from "scmp-app/components/article/article-image/styles";
import { ArticleLeadingVideoContainer } from "scmp-app/components/article/article-leading/styles";
import { ArticleContentRightSection } from "scmp-app/components/article/article-render/article-content/article-content-right-section";
import { pageStyle } from "scmp-app/components/article/article-render/article-content/standard-styles";
import {
  StyledArticleCarouselWidget,
  StyledArticleFurtherReadingWidget,
  StyledContentHeadline,
  StyledContentHeadlineTag,
} from "scmp-app/components/article/article-render/styles";
import {
  SectionContainer,
  SectionLink,
  SectionTagsSpacer,
} from "scmp-app/components/article/article-sections/styles";
import { StyledTopicLink } from "scmp-app/components/article/article-topic/styles";
import { StyledImage as AuthorAvatarImage } from "scmp-app/components/author/author-avatar/styles";
import {
  StyledEntityLink as AuthorNameEntityLink,
  StyledAuthorName,
} from "scmp-app/components/author/author-name/styles";
import { InstagramContainer } from "scmp-app/components/schema-render/common/blockquote/styles";
import { Container as PhotoGalleryContainer } from "scmp-app/components/schema-render/common/div/photo-gallery/styles";
import { StyledDiv as StyledDivContainer } from "scmp-app/components/schema-render/common/div/styles";
import { StyledIframe as StyledGenericIframe } from "scmp-app/components/schema-render/common/iframe/generic-iframe/styles";
import {
  InlineOutstreamAdSlot,
  InlineSlot,
  Container as StyledInlineAdSlotContainer,
} from "scmp-app/components/schema-render/common/inline-ad-slot/styles";
import { StyledParagraph } from "scmp-app/components/schema-render/common/paragraph/styles";
import {
  Caption,
  StyledSummary,
  StyledTable,
  StyledContainer as StyledTableContainer,
  TableFooter,
} from "scmp-app/components/schema-render/common/table/styles";
import {
  StyledLi,
  StyledTbody,
  StyledTd,
  StyledTh,
  StyledThead,
  StyledTr,
  StyledUl,
} from "scmp-app/components/schema-render/content/styles";
import {
  StyledVideoRecorder,
  VideoTitle,
  VideoTitleContainer,
} from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-footer/styles";
import { Container as SCMPYouTubeContainer } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-player/styles";
import {
  Container as SCMPYoutubeVideoContainer,
  StyledSCMPYouTubeVideoFooter,
} from "scmp-app/components/scmp-youtube-video/styles";
import {
  SponsorName,
  SponsorTagContainer,
  SponsorType,
} from "scmp-app/components/sponsor-tag/styles";

import type { ArticleVariant } from ".";

type GenericArticleContainerProps = {
  $articleVariant: ArticleVariant;
};
export const GenericArticleContainer = styled.div<GenericArticleContainerProps>`
  --content-tablet-up-max-inline-size: 600px;
  --widget-extra-padding: 12px;
  --mobile-widget-gap: 16px;
  --desktop-widget-gap: 18px;

  display: grid;
  grid:
    "header-container" min-content
    "leading" min-content
    "content-container" min-content
    "widgets-bottom" min-content
    / 100%;

  inline-size: 100%;
  max-inline-size: 800px;
  margin-inline: auto;
  ${props => {
    switch (props.$articleVariant) {
      case "opinion":
        return css`
          ${theme.breakpoints.only("mobile")} {
            max-inline-size: 600px;

            ${ArticleImageContainer}, ${BottomCarousel} {
              inset-inline: 0;

              inline-size: 100%;
              margin-inline: auto;
            }

            ${BottomCarousel} > .swiper {
              padding-inline: 0;
            }
          }
        `;
    }
  }}

  ${theme.breakpoints.up("tablet")} {
    max-inline-size: 800px;
  }

  ${theme.breakpoints.up("desktop")} {
    grid:
      "header-ad header-ad header-ad header-ad" min-content
      ". header-container header-container header-container" min-content
      ". leading . right" min-content
      ". content-container . right" min-content
      ". . . right" min-content
      ". widgets-bottom widgets-bottom widgets-bottom" min-content
      / minmax(0, 125px) minmax(var(--content-tablet-up-max-inline-size), 712px) 72px 300px;

    min-inline-size: 990px;
    max-inline-size: 1208px;
    margin-inline: auto;
  }
`;

export const HeaderContainer = styled.div`
  grid-area: header-container;

  display: grid;
  grid:
    "topic-and-section" min-content
    "columnist" min-content
    "headline" min-content
    "sub-headline" min-content
    "sponsor" min-content
    "reading-time" min-content
    "action-widgets" min-content
    "google-news-button" min-content
    / 100%;

  inline-size: 100%;
  margin-block-start: 16px;

  & > * {
    margin-block-start: 12px;
  }

  ${theme.breakpoints.up("tablet")} {
    grid:
      "topic-and-section" min-content
      "columnist" min-content
      "headline" min-content
      "sub-headline" min-content
      "sponsor" min-content
      "reading-time" min-content
      "action-widgets" min-content
      / 100%;

    max-inline-size: var(--content-tablet-up-max-inline-size);
    margin-block-start: 40px;
    margin-inline: auto;

    & > * {
      margin-block-start: 16px;
    }
  }

  ${theme.breakpoints.up("desktop")} {
    grid:
      ". topic-and-section . ." min-content
      ". columnist . ." min-content
      ". headline . ." min-content
      ". sub-headline . ." min-content
      ". sponsor . ." min-content
      ". reading-time . ." min-content
      ". action-widgets . ." min-content
      / minmax(0, 56px) 848px 124px minmax(0, 56px);

    max-inline-size: none;
    margin-inline: unset;
  }
`;

export const ContentContainer = styled.div`
  grid-area: content-container;

  display: grid;
  grid:
    "author" min-content
    "author-short-bio" min-content
    "publish-datetime" min-content
    "series-inline" min-content
    "correction" min-content
    "summary" min-content
    "content" min-content
    "paywall" min-content
    "series-carousel" min-content
    "author-detail" min-content
    "related-topic" min-content
    "mobile-footer-ad" min-content
    "bottom-carousel" min-content
    / 100%;

  inline-size: 100%;

  ${theme.breakpoints.up("tablet")} {
    grid:
      "author" min-content
      "author-short-bio" min-content
      "publish-datetime" min-content
      "series-inline" min-content
      "correction" min-content
      "summary" min-content
      "content" min-content
      "paywall" min-content
      "series-carousel" min-content
      "author-detail" min-content
      "related-topic" min-content
      "bottom-carousel" min-content
      / 100%;
  }

  ${theme.breakpoints.up("desktop")} {
    grid:
      ". author ." min-content
      ". author-short-bio ." min-content
      ". publish-datetime ." min-content
      ". series-inline ." min-content
      ". correction ." min-content
      ". summary ." min-content
      ". content ." min-content
      "paywall paywall paywall" min-content
      ". series-carousel ." min-content
      ". author-detail ." min-content
      ". related-topic ." min-content
      / minmax(0, 56px) var(--content-tablet-up-max-inline-size) minmax(0, 56px);
  }
`;

export const RightSectionContainer = styled.div<WithStickyStackedHeightProps>`
  display: flex;
  flex-flow: column nowrap;
  gap: 40px;

  ${AdSlotOuterContainer} {
    block-size: auto;

    ${AdSlotContainer} {
      display: flex;
      align-items: flex-start;

      block-size: 100%;

      ${StyledSlot}:not(${StyledArticleFurtherReadingWidget} ${StyledSlot}) {
        position: sticky;
        inset-block-start: ${props => props.$stickyStackedHeight + 40}px;

        block-size: auto;
      }
    }
  }
`;

export const FurtherReadingRightSectionContainer = styled(RightSectionContainer)`
  flex-grow: 1;
`;

export const RightSectionPlaceholder = styled.div`
  flex: auto;
`;

export const StyledArticleContentRightSection = styled(ArticleContentRightSection)`
  grid-area: right;

  margin-block-start: 32px;

  ${theme.breakpoints.between("mobile", "desktop")} {
    display: none;
  }
`;

export const TopicAndSection = styled.div`
  grid-area: topic-and-section;

  display: flex;
  flex-flow: row wrap;
  gap: 5px 12px;
  justify-content: flex-start;
  align-items: flex-start;

  margin-block-start: 0;

  ${StyledTopicLink} {
    padding-block: 8px;

    color: #404040;
    font-weight: 700;
    font-size: 12px;
    font-family: ${fontRoboto};
    font-style: normal;
    line-height: 100%;
    letter-spacing: -0.12px;

    ${theme.breakpoints.up("tablet")} {
      font-size: 13px;
      letter-spacing: -0.13px;
    }

    :hover {
      color: #2c4692;
    }
  }

  ${SectionContainer} {
    display: flex;
    align-items: center;

    block-size: 29px;
    border-block-end: 1px transparent solid;
  }

  ${SectionTagsSpacer} {
    margin-inline: 4px;
  }

  ${SectionLink} {
    font-size: 12px;
    line-height: 100%;

    ${theme.breakpoints.up("tablet")} {
      font-size: 13px;
    }

    span {
      color: #404040;
      font-weight: 400;
      font-size: inherit;
      font-family: ${fontRoboto};
      font-style: normal;
      line-height: inherit;

      :hover {
        color: #2c4692;
      }
    }
  }
`;

export const Columnist = styled.div`
  grid-area: columnist;

  display: flex;
  flex-flow: row wrap;
  gap: 16px;
  align-items: center;

  ${StyledAuthorAvatar} {
    ${AuthorAvatarImage} {
      padding: 0;
    }
  }

  ${StyledContentHeadlineTag} {
    margin-block-end: 2px;

    font-weight: 700;
    font-size: 16px;
    font-family: ${fontSourceSerif4};
    font-style: normal;
    line-height: 100%;
    letter-spacing: -0.14px;

    ${theme.breakpoints.up("tablet")} {
      margin-block-end: 0;

      font-size: 20px;
      letter-spacing: -0.2px;

      &:after {
        content: "|";

        margin-inline: 8px;

        color: #333333;
      }
    }
  }

  ${AuthorNamesContainer} {
    ${StyledAuthorName}, ${AuthorNameEntityLink}, ${CommaSeparator}, ${AndSeparator} {
      color: #333333;
      font-weight: 600;
      font-size: 14px;
      font-family: ${fontSourceSerif4};
      font-style: normal;
      line-height: 100%;
      font-variant-numeric: lining-nums proportional-nums;

      letter-spacing: -0.14px;

      ${theme.breakpoints.up("tablet")} {
        font-size: 20px;
        letter-spacing: -0.2px;
      }
    }

    ${CommaSeparator}, ${AndSeparator} {
      font-weight: 400;
    }
  }
`;

export const ColumnistWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${theme.breakpoints.up("tablet")} {
    flex-direction: row;
  }
`;

type HeadlineProps = {
  $articleVariant: ArticleVariant;
};
export const Headline = styled.div<HeadlineProps>`
  grid-area: headline;

  color: #000000;
  font-weight: 600;
  font-size: 24px;
  font-family: ${fontSourceSerif4};
  font-style: normal;
  line-height: 120%;
  letter-spacing: -0.6px;

  ${theme.breakpoints.up("tablet")} {
    font-size: 32px;
    letter-spacing: -0.8px;
  }

  ${theme.breakpoints.up("desktop")} {
    font-size: 40px;
    letter-spacing: -1px;
  }

  ${props => {
    switch (props.$articleVariant) {
      // TODO: Will add back postMag style later: case "post-magazine"
      case "opinion":
        return css`
          max-inline-size: 600px;

          font-weight: 400;
          font-family: ${fontCreteRound};

          ${StyledContentHeadline} {
            font-weight: 400;
            font-size: 24px;
            font-family: ${fontCreteRound};
            line-height: 120%;

            ${theme.breakpoints.up("tablet")} {
              font-size: 28px;
            }

            ${theme.breakpoints.up("desktop")} {
              font-size: 36px;
            }
          }
        `;
      case "this-week-in-asia":
        return css`
          margin-block-start: 10px;
          ${theme.breakpoints.up("tablet")} {
            margin-block-start: 14px;
          }

          ${StyledContentHeadline} {
            font-weight: 800;
            font-size: 26px;
            font-family: ${fontHeebo};
            line-height: 33px;

            ${theme.breakpoints.up("tablet")} {
              font-size: 40px;
              line-height: 48px;
            }
          }
        `;
    }
  }}
`;

export const SubHeadline = styled.div`
  grid-area: sub-headline;

  max-inline-size: var(--content-tablet-up-max-inline-size);

  color: #333333;
  font-weight: 500;
  font-size: 18px;
  font-family: ${fontSourceSerif4};
  font-style: normal;
  line-height: 130%;
  letter-spacing: -0.45px;
  font-variant-numeric: lining-nums proportional-nums;

  :empty {
    display: none;
  }

  ${theme.breakpoints.up("tablet")} {
    font-size: 20px;
    letter-spacing: -0.5px;
  }

  ${theme.breakpoints.up("desktop")} {
    font-size: 22px;
    letter-spacing: -0.55px;
  }

  ${StyledLi} {
    ::before {
      display: none;
    }
  }

  ${StyledUl} {
    padding-inline-start: 0;

    ${StyledLi} + ${StyledLi} {
      margin-block-start: 12px;

      ${theme.breakpoints.up("tablet")} {
        margin-block-start: 16px;
      }
    }
  }
`;

export const Sponsor = styled.div`
  grid-area: sponsor;

  :empty {
    display: none;
  }

  ${SponsorTagContainer} {
    display: inline-flex;

    padding: 8px;

    background-color: #f8f8f8;

    ${SponsorType}, ${SponsorName} {
      color: #5a5a5a;
      font-size: 12px;
      font-family: ${fontRoboto};
      font-style: normal;
      line-height: normal;
      font-variant-numeric: lining-nums proportional-nums;

      ${theme.breakpoints.up("tablet")} {
        font-size: 13px;
      }
    }

    ${SponsorType} {
      font-weight: 400;
    }

    ${SponsorName} {
      font-weight: 700;
    }
  }
`;

export const ReadingTime = styled.div`
  grid-area: reading-time;

  display: flex;
  flex-flow: row;
`;

export const ActionWidgets = styled.div`
  grid-area: action-widgets;

  display: flex;
  flex-flow: row wrap;
  gap: 12px;
  align-items: center;

  ${theme.breakpoints.only("mobile")} {
    flex-direction: column;
    align-items: flex-start;
  }

  ${theme.breakpoints.up("tablet")} {
    column-gap: 24px;
  }
`;

export const Leading = styled.div`
  grid-area: leading;

  margin-block: 16px;

  ${theme.breakpoints.up("tablet")} {
    margin-block: 32px;
  }

  ${ArticleImageDescriptionContainer} {
    padding: 0;
  }

  ${ArticleLeadingVideoContainer} {
    block-size: auto;

    background-color: transparent;
  }

  ${StyledSCMPYouTubeVideoFooter} {
    display: flex;
    justify-content: flex-start;
    align-items: center;

    max-inline-size: var(--content-tablet-up-max-inline-size);
    margin-inline: auto;

    ${StyledVideoRecorder} {
      path {
        fill: #666666;
      }
    }

    ${VideoTitle} {
      color: #666666;
      font-size: 16px;
      line-height: 22.4px;
    }
  }
`;

export const ActionWidgetsBar = styled.div`
  grid-area: action-widgets-bar;

  display: flex;
  flex-flow: row nowrap;
  gap: 16px;
  align-items: center;
`;

export const Author = styled.div`
  grid-area: author;

  display: flex;
  justify-content: space-between;

  border-block-start: 1px solid #000000;

  padding-block-start: 12px;

  ${theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    max-inline-size: var(--content-tablet-up-max-inline-size);
    margin-inline: auto;
    padding-block-start: 16px;
  }

  ${AuthorNamesContainer} {
    line-height: normal;
    ${StyledAuthorName}, ${AuthorNameEntityLink}, ${AuthorLocation}, ${CommaSeparator}, ${AndSeparator} {
      color: #000000;
      font-size: 14px;
      font-family: ${fontRoboto};
      font-style: normal;
      line-height: normal;
      font-variant-numeric: lining-nums proportional-nums;
    }

    ${AuthorLocation} {
      color: #000000;
      font-weight: 400;
    }

    ${CommaSeparator}, ${AndSeparator} {
      font-weight: 700;
    }

    ${StyledAuthorName}, ${AuthorNameEntityLink} {
      text-decoration-line: underline;

      font-weight: 700;
    }
  }
`;

export const PublishDateTime = styled.div`
  grid-area: publish-datetime;

  margin-block-start: 8px;

  line-height: normal;

  ${theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    max-inline-size: var(--content-tablet-up-max-inline-size);
    margin-inline: auto;
  }
`;

export const SeriesInline = styled.div`
  grid-area: series-inline;

  margin-block-start: 20px;

  :empty {
    display: none;
  }

  ${theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    max-inline-size: var(--content-tablet-up-max-inline-size);
    margin-block-start: 32px;
    margin-inline: auto;
  }
`;

export const SeriesCarousel = styled.div`
  grid-area: series-carousel;

  margin-block-start: 32px;

  :empty {
    display: none;
  }

  :empty {
    display: none;
  }

  ${theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    max-inline-size: var(--content-tablet-up-max-inline-size);
    margin-inline: auto;
  }
`;

export const Correction = styled.div`
  grid-area: correction;

  display: flex;

  margin-block-start: 16px;

  :empty {
    display: none;
  }

  ${theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    max-inline-size: var(--content-tablet-up-max-inline-size);
    margin-block-start: 32px;
    margin-inline: auto;
  }

  ${theme.breakpoints.up("desktop")} {
    margin-block-start: 32px;
  }

  ${LatestCorrectionDateContainer} {
    display: flex;
    align-items: center;

    inline-size: fit-content;
    padding-block: 6px;
    padding-inline: 8px;

    background-color: #ecf3ff;
  }

  ${LatestCorrectionDateContainer}:after {
    position: relative;

    margin-block-start: 0;
  }

  ${LatestCorrectionDate}, ${Item} {
    font-size: 12px;
    line-height: normal;
    text-decoration: none;

    ${theme.breakpoints.up("tablet")} {
      font-size: 14px;
    }
  }
`;

const iframeWithYoutubeSourceSelector = `iframe[src*="https://www.youtube.com"]`;

type ContentProps = {
  $isShowAdSlot?: boolean;
};

export const Content = styled.div<ContentProps>`
  grid-area: content;

  margin-block-start: 16px;

  ${theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    max-inline-size: var(--content-tablet-up-max-inline-size);
    margin-block-start: 32px;
    margin-inline: auto;
  }

  ${theme.breakpoints.up("desktop")} {
    margin-block-start: 32px;
  }

  ${props => pageStyle(props)}

  ${PhotoGalleryContainer} {
    margin-block: var(--mobile-widget-gap, 16px);

    ${theme.breakpoints.up("desktop")} {
      margin-block: var(--desktop-widget-gap, 18px);
      padding-block: var(--widget-extra-padding, 12px);
    }
  }

  h1,
  h2,
  h3 {
    padding-block: 0 8px;

    font-size: 20px;
    line-height: 26px;

    ${theme.breakpoints.up("tablet")} {
      padding-block: 0 12px;

      font-size: 24px;
      line-height: 31.2px;
    }

    ${StyledAnchor} {
      color: #000000;
      font-weight: 700;
      font-size: 20px;
      font-family: ${fontRoboto};
      line-height: 26px;
      text-decoration-color: #2c4692;
      ${theme.breakpoints.up("tablet")} {
        font-size: 24px;
        line-height: 31.2px;
      }
    }
  }

  h4 {
    padding-block: 0 8px;

    font-size: 16px;
    line-height: 20.8px;

    ${theme.breakpoints.up("tablet")} {
      font-size: 20px;
      line-height: 26px;
    }

    ${StyledAnchor} {
      color: #000000;
      font-weight: 700;
      font-size: 16px;
      font-family: ${fontRoboto};
      line-height: 20.8px;
      text-decoration-color: #2c4692;
      ${theme.breakpoints.up("tablet")} {
        font-size: 20px;
        line-height: 26px;
      }
    }
  }

  ${StyledParagraph} {
    font-size: 16px;
    line-height: 160%;

    ${theme.breakpoints.up("desktop")} {
      font-size: 18px;
    }
  }

  ${StyledAnchor} {
    color: #2c4692;
    font-weight: 400;
    font-size: 16px;
    font-family: ${fontMerriweather};
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }

    > span {
      border-block-end: 1px solid transparent;

      &:hover {
        color: #2c4692;
      }
    }

    ${theme.breakpoints.up("desktop")} {
      font-size: 18px;
    }
  }

  .methode-html-wrapper {
    margin-block: var(--mobile-widget-gap, 16px);
    padding-block: var(--widget-extra-padding, 12px);

    ${theme.breakpoints.up("desktop")} {
      margin-block: var(--desktop-widget-gap, 18px);
    }

    ${StyledGenericIframe} {
      /** horizontally center align the content of Facebook Post */
      &[src*="www.facebook.com/plugins/post"] {
        ${theme.breakpoints.up("tablet")} {
          inline-size: revert-layer;
          margin-inline: auto;
        }
      }
    }

    ${InstagramContainer} {
      ${theme.breakpoints.up("desktop")} {
        margin-block: 0;
      }
    }
  }

  .video-wrapper {
    margin-block: var(--mobile-widget-gap, 16px);
    padding-block: var(--widget-extra-padding, 12px);

    ${theme.breakpoints.up("desktop")} {
      margin-block: var(--desktop-widget-gap, 18px);
    }
  }

  ${StyledDivContainer}:has(${iframeWithYoutubeSourceSelector}), ${SCMPYoutubeVideoContainer} {
    aspect-ratio: auto;

    ${StyledGenericIframe} {
      &[src*="https://www.youtube.com"] {
        min-inline-size: 240px;
        max-inline-size: var(--content-tablet-up-max-inline-size);
        margin-inline: auto;
        aspect-ratio: 16 / 9;

        ${theme.breakpoints.up("tablet")} {
          inline-size: 100%;
          max-inline-size: none;
        }
      }
    }

    ${SCMPYouTubeContainer} {
      --portrait-inline-size: 335px;
      margin-inline: auto;

      ${theme.breakpoints.only("mobile")} {
        --portrait-inline-size: 280px;
      }
    }

    ${VideoTitleContainer} {
      margin-block: 0;
      margin-inline: auto;
      padding-block: 12px 11px;
      border-block-end: 1px solid #000000;

      ${theme.breakpoints.up("tablet")} {
        align-items: center;
      }

      ${VideoTitle} {
        color: #000000;
        font-size: 16px;
        line-height: 22.4px;
      }
    }
  }

  ${StyledTableContainer} {
    margin-block: 20px;
    padding-block: 12px;

    ${StyledTable} {
      table-layout: fixed;
    }

    ${theme.breakpoints.up("tablet")} {
      padding-block: 16px;
    }

    ${StyledSummary} {
      inline-size: fit-content;
      margin-block-end: 16px;
      padding-block-start: 7px;

      font-size: 16px;
      line-height: 19px;
      border-block-start: 1px solid #000000;
    }

    ${Caption} {
      margin-block-end: 16px;

      color: #000000;
      font-size: 14px;
      line-height: 16px;
    }

    ${StyledThead} {
      ${StyledTr} {
        background-color: #c8ced5;

        ${StyledTh} {
          min-inline-size: 83px;
          padding-block: 16px;
          padding-inline: 12px;

          font-size: 14px;
          line-height: 16px;
          text-align: start;
          word-wrap: break-word;
        }
      }
    }

    ${StyledTbody} {
      ${StyledTr} {
        &:nth-child(odd) {
          background-color: #f2f2f2;
        }
        &:nth-child(even) {
          background-color: #ffffff;
        }
      }

      ${StyledTd} {
        min-inline-size: 83px;
        padding-block: 16px;
        padding-inline: 12px;

        font-size: 14px;
        line-height: 16px;
        text-align: start;
        word-wrap: break-word;
      }
    }

    ${TableFooter} {
      display: flex;
    }
  }

  ${ArticleImageContainer} {
    position: static;
    inset-inline: auto;

    inline-size: auto;
    margin-block: var(--mobile-widget-gap, 16px);
    margin-inline: 0;

    ${theme.breakpoints.up("desktop")} {
      margin-block: var(--desktop-widget-gap, 18px);
      padding-block: var(--widget-extra-padding, 12px);
    }

    ${DescriptionContainer} {
      padding-inline: 0;

      color: #727272;
      font-size: 14px;
      line-height: 18.2px;

      ${theme.breakpoints.up("tablet")} {
        font-size: 15px;
        line-height: 19.5px;
      }
    }
  }

  ${StyledInlineAdSlotContainer}:has(+ *) {
    margin-block-end: var(--mobile-widget-gap, 16px);

    ${theme.breakpoints.up("desktop")} {
      margin-block-end: var(--desktop-widget-gap, 18px);
    }

    /** hide element to remove margin when the ad is hidden */
    &:has(div[data-size="[[1,1]]"]) {
      margin-block-end: 0;
    }
  }

  ${props =>
    !props.$isShowAdSlot &&
    css`
      ${InlineSlot} {
        display: none;
      }
    `}

  ${InlineSlot}, ${InlineOutstreamAdSlot}:has(${StyledSlot}:not(.${getClassNameFromSize({
    height: 0,
    width: 0,
  })})), ${InlineOutstreamAdSlot}:has(${StyledSlot}:not(.${getClassNameFromSize({
    height: 1,
    width: 1,
  })})) {
    inline-size: 100vw;
    margin-block: var(--widget-extra-padding, 12px);
    margin-inline: calc(50% - 50vw);
    padding-block-end: 12px;
    border-block: 1px solid #dfdfdf;

    ${StyledSlot}[data-size="[[1,1]]"] {
      inline-size: 1px;
      block-size: 1px;
    }
  }

  ${InlineSlot}:has(${StyledSlot}[data-size="[[1,1]]"]) {
    margin: 0;
    padding: 0;

    border: none;

    background-color: transparent;
  }

  /** The generic article out stream ad slot margin is defined in OutstreamAdSlot-Container */
  ${InlineOutstreamAdSlot} {
    :not(
      .${getClassNameFromSize({ height: 0, width: 0 })},.${getClassNameFromSize({
          height: 1,
          width: 1,
        })}
    ) {
      margin-block: 0;
    }
  }
`;

export const MoreOnThis = styled.div`
  grid-area: more-on-this;

  margin-block-start: 40px;

  :empty {
    display: none;
  }

  ${theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    max-inline-size: var(--content-tablet-up-max-inline-size);
    margin-inline: auto;
  }

  ${theme.breakpoints.up("desktop")} {
    display: none;
  }
`;

export const BottomCarousel = styled.div`
  grid-area: bottom-carousel;

  margin-block-start: 40px;

  :empty {
    display: none;
  }

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: var(--mobile-widget-gap, 16px);

    ${setFullViewportWidthStyles()}

    ${StyledArticleCarouselWidget} {
      padding-inline: 20px;
    }
  }

  ${theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    max-inline-size: var(--content-tablet-up-max-inline-size);
    margin-block-start: var(--mobile-widget-gap, 16px);
    margin-inline: auto;
  }

  ${theme.breakpoints.up("desktop")} {
    display: none;
  }
`;

export const AuthorDetail = styled.div`
  grid-area: author-detail;

  margin-block-start: var(--widget-extra-padding, 12px);
  padding-block-start: 12px;
  border-block-start: 1px solid #000000;

  :empty {
    display: none;
  }

  ${theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    max-inline-size: var(--content-tablet-up-max-inline-size);
    margin-inline: auto;
  }

  ${theme.breakpoints.up("desktop")} {
    padding-block-start: 16px;
  }
`;

export const RelatedTopic = styled.div`
  grid-area: related-topic;

  inline-size: 100%;
  margin-block-start: var(--mobile-widget-gap, 16px);
  padding-block-start: 12px;
  border-block-start: 1px solid #dfdfdf;

  &:empty {
    display: none;
  }

  ${theme.breakpoints.up("tablet")} {
    max-inline-size: var(--content-tablet-up-max-inline-size);
    margin-inline: auto;
  }

  ${theme.breakpoints.up("desktop")} {
    margin-block-start: var(--desktop-widget-gap, 18px);
  }
`;

export const WidgetsBottom = styled.div`
  grid-area: widgets-bottom;
`;

export const DesktopHeaderAdSlot = styled(ArticleHeaderAdSlot)`
  grid-area: header-ad;

  display: none;

  ${theme.breakpoints.up("desktop")} {
    display: block;

    margin-inline: calc(50% - 50vw);

    background: #f8f8f8;
  }

  ${AdSlotContainer} {
    display: flex;
    align-items: center;
  }
`;

export const RightAvailableSpaceTracker = styled.div`
  grid-row: leading-start / author-detail-end;
  grid-column: span 1;

  display: none;

  inline-size: 0;

  ${theme.breakpoints.up("desktop")} {
    display: flex;
  }
`;

export const FurtherReadingInlineContainer = styled.div`
  display: flex;
  justify-content: center;

  margin-block: var(--mobile-widget-gap, 16px);
  padding-block: var(--widget-extra-padding, 12px);

  :empty {
    display: none;
  }

  ${theme.breakpoints.up("desktop")} {
    display: none;
  }
`;

export const PaywallContainer = styled.div`
  grid-area: paywall;
`;

export const StyledArticleGoogleNewsButton = styled(ArticleGoogleNewsButton)`
  grid-area: google-news-button;
`;

export const AuthorShortBio = styled.div`
  grid-area: author-short-bio;

  margin-block: 4px;

  color: #5a5a5a;
  font-size: 13px;
  font-family: ${fontRoboto};
  line-height: 130%;
  font-variant-numeric: lining-nums proportional-nums;

  ${theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    max-inline-size: var(--content-tablet-up-max-inline-size);
    margin-inline: auto;

    font-size: 14px;
  }

  &:empty {
    display: none;
  }
`;
